// <juro-racket-3d> — self-contained Three.js viewer for the JURO AIM racket.
// Sizes to its own container (not the window) and only renders while on-screen.
// Loaded via <x-import component-from-global-scope="juro-racket-3d" from="./juro-racket-3d.js">.
// Requires an importmap in the page for "three" + "three/addons/".
(function () {
  if (customElements.get('juro-racket-3d')) return;

  class JuroRacket3D extends HTMLElement {
    connectedCallback() {
      if (this._booted) return;
      this._booted = true;

      this.style.display = 'block';
      this.style.position = 'relative';
      this.style.width = this.style.width || '100%';
      this.style.background = '#f2ebe7';

      this.innerHTML = `
        <canvas class="jr3d-c" style="display:block;width:100%;height:100%;touch-action:none;cursor:grab"></canvas>
        <div class="jr3d-loader" style="position:absolute;inset:0;display:grid;place-items:center;background:#f2ebe7;color:#5c6675;font-family:'Switzer',system-ui,sans-serif;font-size:11px;letter-spacing:.2em;text-transform:uppercase;transition:opacity .5s;z-index:5">Loading 3D…</div>
      `;

      const canvas = this.querySelector('.jr3d-c');
      canvas.addEventListener('mousedown', () => (canvas.style.cursor = 'grabbing'));
      window.addEventListener('mouseup', () => (canvas.style.cursor = 'grab'));

      this._boot(canvas).catch((err) => {
        console.error('[juro-racket-3d]', err);
        const l = this.querySelector('.jr3d-loader');
        if (l) l.textContent = '3D unavailable';
      });
    }

    async _boot(canvas) {
      const THREE = await import('three');
      const { OrbitControls } = await import('three/addons/controls/OrbitControls.js');
      const { STLLoader } = await import('three/addons/loaders/STLLoader.js');
      const { RoomEnvironment } = await import('three/addons/environments/RoomEnvironment.js');
      const { mergeVertices, toCreasedNormals } = await import('three/addons/utils/BufferGeometryUtils.js');
      const { DecalGeometry } = await import('three/addons/geometries/DecalGeometry.js');

      const NAVY = 0x061224;
      const host = this;

      const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true, preserveDrawingBuffer: true });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.toneMappingExposure = 1.0;

      const scene = new THREE.Scene();
      scene.background = new THREE.Color(0xf2ebe7);
      const pmrem = new THREE.PMREMGenerator(renderer);
      scene.environment = pmrem.fromScene(new RoomEnvironment(renderer), 0.04).texture;

      const camera = new THREE.PerspectiveCamera(32, 1, 1, 500);
      camera.position.set(15, -5, 80);

      const controls = new OrbitControls(camera, canvas);
      controls.enableDamping = true;
      controls.dampingFactor = 0.06;
      controls.minDistance = 30;
      controls.maxDistance = 140;
      controls.target.set(0, -8, 0);
      controls.autoRotate = true;
      controls.autoRotateSpeed = 1.1;
      controls.enablePan = false;
      controls.enableZoom = false;
      let targetDist = camera.position.distanceTo(controls.target);
      const wheelRaycaster = new THREE.Raycaster();
      const wheelNdc = new THREE.Vector2();
      canvas.addEventListener('wheel', (e) => {
        // Only zoom the model when the cursor is actually over the racket —
        // scrolling anywhere else on the canvas should just scroll the page.
        const rect = canvas.getBoundingClientRect();
        wheelNdc.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
        wheelNdc.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
        wheelRaycaster.setFromCamera(wheelNdc, camera);
        const hit = wheelRaycaster.intersectObject(racket, true).length > 0;
        if (!hit) return;
        e.preventDefault();
        targetDist = THREE.MathUtils.clamp(targetDist * (1 + e.deltaY * 0.0011), controls.minDistance, controls.maxDistance);
      }, { passive: false });
      let interacted = false;
      canvas.addEventListener('pointerdown', () => {
        controls.autoRotate = false;
        if (!interacted) {
          interacted = true;
          const h = host.querySelector('.jr3d-hint');
          if (h) h.style.opacity = 0;
        }
      });

      const key = new THREE.DirectionalLight(0xffffff, 1.5);
      key.position.set(24, 30, 32);
      scene.add(key);
      const fill = new THREE.DirectionalLight(0xdfe8f5, 1.0);
      fill.position.set(-26, -6, -20);
      scene.add(fill);
      const fill2 = new THREE.DirectionalLight(0xeef3fb, 0.9);
      fill2.position.set(-28, 14, 26);
      scene.add(fill2);
      const rim = new THREE.DirectionalLight(0xffffff, 0.7);
      rim.position.set(0, 8, -34);
      scene.add(rim);
      scene.add(new THREE.AmbientLight(0xbfcbe0, 0.55));
      scene.add(new THREE.HemisphereLight(0xffffff, 0x2a3550, 0.5));

      const racket = new THREE.Group();
      scene.add(racket);

      const shellMat = new THREE.MeshPhysicalMaterial({ color: NAVY, roughness: 0.62, metalness: 0.0, clearcoat: 0.0 });

      // shared white JURO wordmark (used on the face + both rim sides)
      const LOGO_AR = 2.716; // wordmark width / height
      const logoImg = new Image();
      logoImg.crossOrigin = 'anonymous';
      logoImg.src = (window.__resources && window.__resources.juroWordmark) || 'assets/juro-wordmark-white.png';
      const onLogoReady = (fn) => {
        if (logoImg.complete && logoImg.naturalWidth) fn();
        else logoImg.addEventListener('load', fn);
      };

      let CY = 13.5;
      let racketMesh = null;
      new STLLoader().load((window.__resources && window.__resources.racketStl) || 'assets/juro-racket.stl', (geometry) => {
        geometry = mergeVertices(geometry, 1e-3);
        geometry = toCreasedNormals(geometry, Math.PI / 6);
        geometry.scale(0.1, 0.1, 0.1);
        racketMesh = new THREE.Mesh(geometry, shellMat);
        racket.add(racketMesh);
        // ---- auto-frame: fit the full racket length into view ----
        geometry.computeBoundingBox();
        const _bb = geometry.boundingBox;
        const _sizeY = _bb.max.y - _bb.min.y;
        const _centerY = (_bb.max.y + _bb.min.y) / 2;
        const _centerX = (_bb.max.x + _bb.min.x) / 2;
        const _centerZ = (_bb.max.z + _bb.min.z) / 2;
        CY = controls.target.y - _centerY;
        racket.position.y = CY;
        // orbit + frame around the model's true centre so it sits dead-centre
        controls.target.x = _centerX;
        controls.target.z = _centerZ;
        const _vFov = camera.fov * Math.PI / 180;
        const _fit = (_sizeY * 1.16 / 2) / Math.tan(_vFov / 2);
        targetDist = THREE.MathUtils.clamp(_fit, controls.minDistance, controls.maxDistance);
        const _dir = camera.position.clone().sub(controls.target).setLength(targetDist);
        camera.position.copy(controls.target).add(_dir);
        camera.updateProjectionMatrix();
        // sit the ground shadow under the racket's real bottom
        shadow.position.set(_centerX, CY + _bb.min.y + 0.5, _centerZ);
        addSideLogos(racketMesh);
        const l = host.querySelector('.jr3d-loader');
        if (l) { l.style.opacity = 0; setTimeout(() => l.remove(), 600); }
      });

      // ---------- FACE MARKINGS ----------
      const R = 7.25;
      const faceRedraws = [];
      function makeFaceTexture(mirror) {
        const S = 1024, c = document.createElement('canvas');
        c.width = c.height = S;
        const g = c.getContext('2d');
        const u = S / 2 / R;
        const X = (x) => S / 2 + (mirror ? -x : x) * u, Y = (y) => S / 2 - y * u;
        const tex = new THREE.CanvasTexture(c);
        tex.anisotropy = 8;
        tex.colorSpace = THREE.SRGBColorSpace;
        const draw = () => {
          g.clearRect(0, 0, S, S);
          g.strokeStyle = g.fillStyle = 'rgba(211,216,222,.95)';
          g.lineWidth = 0.18 * u;
          g.lineCap = 'round';
          const line = (x1, y1, x2, y2) => { g.beginPath(); g.moveTo(X(x1), Y(y1)); g.lineTo(X(x2), Y(y2)); g.stroke(); };
          line(0, 2.05, 0, 5.65);
          line(0, -2.05, 0, -5.65);
          line(3.35, 0, 5.65, 0);
          line(-3.35, 0, -5.65, 0);
          const b = 1.41, a = 0.78;
          for (const [sx, sy] of [[1, 1], [1, -1], [-1, 1], [-1, -1]]) {
            line(sx * b - sx * a, sy * b, sx * b, sy * b);
            line(sx * b, sy * b, sx * b, sy * b - sy * a);
          }
          // JURO wordmark logo near the bottom rim
          if (logoImg.complete && logoImg.naturalWidth) {
            const lw = 3.0 * u, lh = lw / LOGO_AR;
            const cx = S / 2, cy = Y(-5.95);
            g.save();
            g.translate(cx, cy);
            if (mirror) g.scale(-1, 1);
            g.drawImage(logoImg, -lw / 2, -lh / 2, lw, lh);
            g.restore();
          }
          // punch out the drilled holes
          g.globalCompositeOperation = 'destination-out';
          const holesCm = [[0, 0], [0, 4.0], [0, -4.0], [3.46, 2.0], [-3.46, 2.0], [3.46, -2.0], [-3.46, -2.0]];
          for (const [hx, hy] of holesCm) { g.beginPath(); g.arc(X(hx), Y(hy), 0.76 * u, 0, Math.PI * 2); g.fill(); }
          g.globalCompositeOperation = 'source-over';
          tex.needsUpdate = true;
        };
        draw();
        faceRedraws.push(draw);
        return tex;
      }
      const decalGeo = new THREE.CircleGeometry(R, 96);
      const FACE_Z = 1.9;
      const decalF = new THREE.Mesh(decalGeo, new THREE.MeshBasicMaterial({ map: makeFaceTexture(false), transparent: true, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -1 }));
      decalF.position.z = FACE_Z + 0.03;
      racket.add(decalF);
      const decalB = new THREE.Mesh(decalGeo, new THREE.MeshBasicMaterial({ map: makeFaceTexture(false), transparent: true, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -1 }));
      decalB.rotation.y = Math.PI;
      decalB.position.z = -(FACE_Z + 0.03);
      racket.add(decalB);
      racket.position.y = CY;
      onLogoReady(() => faceRedraws.forEach((d) => d()));

      // ---------- HEAD-SIDE LOGO (projected decals onto the real rim) ----------
      const makeSideLogoTex = (mirror) => {
        const CW = 512, CH = Math.round(CW * LOGO_AR);
        const c = document.createElement('canvas');
        c.width = CW; c.height = CH;
        const g = c.getContext('2d');
        g.translate(CW / 2, CH / 2);
        if (mirror) g.scale(-1, 1);
        g.rotate(-Math.PI / 2); // run the wordmark along the long (length) axis
        const lw = CH * 0.94, lh = lw / LOGO_AR;
        if (logoImg.complete && logoImg.naturalWidth) g.drawImage(logoImg, -lw / 2, -lh / 2, lw, lh);
        const tex = new THREE.CanvasTexture(c);
        tex.colorSpace = THREE.SRGBColorSpace;
        tex.anisotropy = 8;
        return tex;
      };
      const addSideLogos = (mesh) => {
        const build = () => {
          const savedY = racket.position.y;
          racket.position.y = 0;
          racket.rotation.set(0, 0, 0);
          racket.updateMatrixWorld(true);
          mesh.updateMatrixWorld(true);
          // decal box: x = rim thickness (world z), y = logo length (world y), z = projection depth (world x)
          const SIZE = new THREE.Vector3(2.4, 6.5, 3.4);
          const one = (sign, mirror) => {
            const pos = new THREE.Vector3(sign * 7.25, 0, 0);
            const orient = new THREE.Euler(0, sign > 0 ? Math.PI / 2 : -Math.PI / 2, 0);
            const geo = new DecalGeometry(mesh, pos, orient, SIZE);
            const mat = new THREE.MeshStandardMaterial({
              map: makeSideLogoTex(mirror), transparent: true, roughness: 0.55, metalness: 0,
              polygonOffset: true, polygonOffsetFactor: -6, polygonOffsetUnits: -6,
            });
            racket.add(new THREE.Mesh(geo, mat));
          };
          one(1, false);
          one(-1, false);
          racket.position.y = savedY;
        };
        if (logoImg.complete && logoImg.naturalWidth) build();
        else onLogoReady(build);
      };
      this._three = { racket, camera, controls };

      // ---------- soft ground shadow ----------
      const shC = document.createElement('canvas'); shC.width = shC.height = 256;
      const sg = shC.getContext('2d');
      const grad = sg.createRadialGradient(128, 128, 8, 128, 128, 120);
      grad.addColorStop(0, 'rgba(15,27,44,.30)');
      grad.addColorStop(1, 'rgba(15,27,44,0)');
      sg.fillStyle = grad; sg.fillRect(0, 0, 256, 256);
      const shTex = new THREE.CanvasTexture(shC);
      const shadow = new THREE.Mesh(new THREE.PlaneGeometry(46, 20), new THREE.MeshBasicMaterial({ map: shTex, transparent: true, depthWrite: false }));
      shadow.rotation.x = -Math.PI / 2;
      shadow.position.y = -25;
      scene.add(shadow);

      // ---------- sizing to container ----------
      const resize = () => {
        const w = this.clientWidth, h = this.clientHeight;
        if (w <= 1 || h <= 1) { requestAnimationFrame(resize); return; }
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        renderer.setSize(w, h, false);
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
      };
      const ro = new ResizeObserver(resize);
      ro.observe(this);
      resize();
      let lastW = 0, lastH = 0;

      // ---------- render only while visible ----------
      let visible = true;
      const io = new IntersectionObserver((es) => { es.forEach((e) => (visible = e.isIntersecting)); }, { threshold: 0.01 });
      io.observe(this);

      const clock = new THREE.Clock();
      renderer.setAnimationLoop(() => {
        if (!visible) return;
        const w = this.clientWidth, h = this.clientHeight;
        if (w > 1 && h > 1 && (w !== lastW || h !== lastH)) { lastW = w; lastH = h; resize(); }
        const t = clock.getElapsedTime();
        racket.position.y = CY + Math.sin(t * 0.9) * 0.35;
        const dir = camera.position.clone().sub(controls.target);
        const curDist = dir.length();
        if (Math.abs(curDist - targetDist) > 0.001) {
          dir.setLength(THREE.MathUtils.lerp(curDist, targetDist, 0.12));
          camera.position.copy(controls.target).add(dir);
        }
        controls.update();
        renderer.render(scene, camera);
      });
    }
  }

  customElements.define('juro-racket-3d', JuroRacket3D);
})();